<template>
    <div>
        <!-- Terms Acceptance Modal -->
        <terms-acceptance-popup 
            :show="showTermsModal" 
            :subscription-type="subscriptionType"
            :plan-slug="getPlanSlug()"
            :no-flash="true"
            :is-processing="isSubscribing && keepTermsModalOpen"
            @close="closeTermsModal"
            @success="handleTermsSuccess"
            @error="handleTermsError"
        ></terms-acceptance-popup>

        <!-- Add Credit Card Modal -->
        <add-card-popup
            :show="showAddCardModal"
            :stripe_data="stripeData"
            confirm-button-text="Subscribe"
            @card-added="handleCardAdded"
            @close="closeCardModal"
        ></add-card-popup>
    </div>
</template>

<script setup>
import { ref, nextTick, watch, onMounted } from 'vue';
import { router } from '@inertiajs/vue3';
import axios from 'axios';
import TermsAcceptancePopup from '@/Shared/TermsAcceptancePopup.vue';
import AddCardPopup from '@/Shared/AddCardPopup.vue';

const props = defineProps({
    subscriptionType: {
        type: String,
        default: 'protect'
    },
    termsAccepted: {
        type: Boolean,
        default: false
    },
    // 86bc9e403 #2: the combined Pro checkout shows its own "By subscribing you agree…" consent
    // line and never routes through the separate Terms pop-up on a retry. Set this so the subscribe
    // request ALWAYS carries terms_accepted — the server records ToS idempotently before the charge
    // (only when none is on record yet), so this can never cause a 422, even if a caller's
    // terms-accepted flag was wrongly flipped true after a terms-POST or network failure that
    // recorded nothing server-side.
    alwaysSendTerms: {
        type: Boolean,
        default: false
    },
    hasPaymentMethod: {
        type: Boolean,
        default: false
    },
    userPaymentMethod: {
        type: Object,
        default: null
    },
    stripeData: {
        type: [Object, Array],
        required: true
    },
    // RAC-10025: confirmation modal removed. Prop kept for backward compat
    // with existing callers; value is ignored. Safe to drop in a follow-up
    // PR once every caller has been cleaned up.
    showConfirmationModal: {
        type: Boolean,
        default: false
    },
    planSlug: {
        type: String,
        default: null
    },
    // 86bcd9hg3: RiskGuard Pro is yearly only.
    billingCycle: {
        type: String,
        default: 'annual'
    },
    planPrice: {
        type: String,
        default: null
    },
    trialDays: {
        type: Number,
        default: 0
    },
    // RAC-10004: lets the parent override the post-subscription redirect.
    // When null, falls back to the legacy "/pricing -> payment-methods.index"
    // / current-page behavior. Used by the signup flow to land users on
    // the property details page they just created.
    successRedirectUrl: {
        type: String,
        default: null,
    },
});

const emit = defineEmits(['success', 'error', 'cancel', 'restore-plan-selection', 'tier-changed']);

// Reactive state
const showTermsModal = ref(false);
const showAddCardModal = ref(false);
const isSubscribing = ref(false);
const pendingApplication = ref(null);
const keepTermsModalOpen = ref(false);

// Store plan data in reactive refs to persist through reloads
const storedPlanSlug = ref(null);
const storedBillingCycle = ref(null);

// Helper to get stored plan data from sessionStorage
const getStoredPlanData = () => {
    try {
        const stored = sessionStorage.getItem('pending_subscription_plan')
        if (stored) {
            return JSON.parse(stored)
        }
    } catch (e) {
        console.error('Error reading stored plan data:', e)
    }
    return null
}

const getStoredSuccessMessage = () => {
    try {
        return sessionStorage.getItem('subscription_success_message')
    } catch (e) {
        console.error('Error reading stored success message:', e)
    }
    return null
}

// Load plan data from sessionStorage into reactive refs
const loadStoredPlanData = () => {
    const stored = getStoredPlanData();
    if (stored) {
        storedPlanSlug.value = stored.planSlug;
        storedBillingCycle.value = stored.billingCycle;
    }
};

// Get plan data from props, reactive refs, or sessionStorage (in that order)
// These functions check all sources to ensure data is available
const getPlanSlug = () => {
    // Read the freshly-stored plan first (as getBillingCycle does): the cached ref can
    // hold a previous attempt's slug, e.g. the old 'protect-pro' (86bcd9hg3).
    const stored = getStoredPlanData();
    if (stored?.planSlug) {
        storedPlanSlug.value = stored.planSlug;
        return stored.planSlug;
    }
    if (storedPlanSlug.value) return storedPlanSlug.value;
    // Finally check props (from parent component) - but only if no stored data
    if (props.planSlug) return props.planSlug;
    return null;
}

// 86bc9e403 #4: the combined Pro checkout passes the entered count + tier straight to
// initiateSubscription / subscribeAfterCardSaved, so a sessionStorage write that threw
// (private mode) can't drop them. Read back only when sessionStorage has nothing.
const pendingPlanFallback = ref(null);

// The Pro tier the pop-up showed the landlord (86bcd9hg3). Always read fresh: after a
// 409 "your price changed" the caller stores the new tier, and resending a stale one
// would be refused again. Falls back to the value the pop-up handed us directly.
const getExpectedTierSlug = () => getStoredPlanData()?.expectedTierSlug ?? pendingPlanFallback.value?.expectedTierSlug ?? null

// A new onboarding landlord with no properties typed how many they manage; the server prices
// the tier from it. The server floors the billed count at the landlord's real countable
// properties, so a sent count can only ever raise the tier, never let an existing landlord
// pay a smaller one. Falls back to the value the pop-up handed us directly.
const getEnteredPropertyCount = () => getStoredPlanData()?.propertyCount ?? pendingPlanFallback.value?.propertyCount ?? null

const getBillingCycle = () => {
    // Read the freshly-stored plan first: storedBillingCycle can hold a stale
    // value from a prior attempt (it's never reset), and the cycle now varies
    // per selection (monthly/annual), so the just-written sessionStorage is the
    // authoritative intent for this checkout.
    const stored = getStoredPlanData();
    if (stored?.billingCycle) {
        // Update reactive refs for future use
        storedBillingCycle.value = stored.billingCycle;
        return stored.billingCycle;
    }
    if (storedBillingCycle.value) return storedBillingCycle.value;
    // Finally check props (from parent component) - but only if no stored data
    if (props.billingCycle && props.billingCycle !== 'monthly') return props.billingCycle;
    return props.billingCycle || 'annual';
}

/**
 * Get the prop name for terms accepted based on subscription type
 */
const getTermsAcceptedProp = () => {
    const propMap = {
        'protect': 'protect_tos_accepted_at',
        'rent_payments': 'rent_payments_tos_accepted_at',
        'maintenance': 'maintenance_tos_accepted_at'
    };
    return propMap[props.subscriptionType] || 'protect_tos_accepted_at';
};

/**
 * Get the prop names for payment method based on subscription type
 */
const getPaymentMethodProps = () => {
    return ['is_agent_payment_method_added', 'user_payment_method'];
};

const closeAllModals = () => {
    showTermsModal.value = false;
    showAddCardModal.value = false;
    keepTermsModalOpen.value = false;
};

/**
 * Main method to initiate the subscription flow
 * This will check terms, card, and subscribe if needed
 *
 * RAC-10025: removed the post-add-card confirmation modal. When the user
 * already has a card + accepted terms, we subscribe immediately. When the
 * add-card flow runs, the AddCardPopup CTA reads "Subscribe" and clicking
 * it both attaches the card AND subscribes — no intermediate confirmation.
 *
 * @param {Object} application - Optional application to navigate to after subscription
 */
const initiateSubscription = async (application = null, planFallback = null) => {
    pendingApplication.value = application;
    pendingPlanFallback.value = planFallback;
    isSubscribing.value = true;
    keepTermsModalOpen.value = false;

    // RAC-10335: capture the caller's plan selection into the reactive ref immediately,
    // synchronously, before any await. Callers set `pending_subscription_plan` in
    // sessionStorage right before calling this; reads deferred to subscribe-time could race
    // (the RAC-10008 footgun) and land without plan_slug — which the backend used to silently
    // treat as base RiskGuard. `router.reload` in this flow uses preserveState, so the ref
    // survives to the eventual subscribeToProtect() call.
    loadStoredPlanData();

    // Case 1: User has card and terms already accepted → subscribe immediately.
    if (props.hasPaymentMethod && props.termsAccepted) {
        subscribeToProtect();
        return;
    }

    // Case 2: User has card but terms not accepted → show terms, then subscribe
    if (props.hasPaymentMethod && !props.termsAccepted) {
        showTermsModal.value = true;
        isSubscribing.value = false;
        return;
    }

    // Case 3: User doesn't have card → show terms first (if needed), then add card
    if (!props.hasPaymentMethod && !props.userPaymentMethod) {
        if (!props.termsAccepted) {
            showTermsModal.value = true;
        } else {
            if (props.stripeData && typeof props.stripeData === 'object' && !Array.isArray(props.stripeData)) {
                showAddCardModal.value = true;
            } else {
                isSubscribing.value = false;
                emit('error', {
                    type: 'card',
                    error: 'Stripe data is not available. Please refresh the page and try again.'
                });
                return;
            }
        }
        isSubscribing.value = false;
        return;
    }

    // Fallback: if somehow we get here, try to subscribe
    isSubscribing.value = false;
    subscribeToProtect();
};

const closeTermsModal = () => {
    if (keepTermsModalOpen.value && isSubscribing.value) {
        return;
    }
    keepTermsModalOpen.value = false;
    showTermsModal.value = false;
    emit('cancel');
};

const handleTermsSuccess = () => {
    const currentScrollY = window.scrollY;

    // Prevent TermsAcceptancePopup from emitting close (which would emit cancel
    // to the parent) while we reload and transition to add-card or subscribe.
    keepTermsModalOpen.value = true;
    isSubscribing.value = true;

    router.reload({
        only: [getTermsAcceptedProp(), ...getPaymentMethodProps()],
        preserveState: true,
        preserveScroll: true,
        onSuccess: async () => {
            await nextTick();
            requestAnimationFrame(() => {
                window.scrollTo(0, currentScrollY);
            });
            
            // Emit event to parent to restore plan selection from sessionStorage
            emit('restore-plan-selection');
            await nextTick();
            
            // Load stored plan data multiple times to ensure it's available
            loadStoredPlanData();
            await nextTick();
            loadStoredPlanData();
            await nextTick();
            
            // Small delay to allow restoration to complete
            await new Promise(resolve => setTimeout(resolve, 200));
            
            // Load one more time after delay
            loadStoredPlanData();
            
            const needsCard = !props.hasPaymentMethod && !props.userPaymentMethod;

            if (needsCard) {
                keepTermsModalOpen.value = false;
                showTermsModal.value = false;
                isSubscribing.value = false;
                if (props.stripeData && typeof props.stripeData === 'object' && !Array.isArray(props.stripeData)) {
                    showAddCardModal.value = true;
                } else {
                    emit('error', {
                        type: 'card',
                        error: 'Stripe data is not available. Please refresh the page and try again.'
                    });
                }
            } else {
                // RAC-10025: terms accepted and card exists → subscribe directly,
                // no intermediate confirmation modal.
                subscribeToProtect();
            }
        }
    });
};

const handleTermsError = ({ error }) => {
    isSubscribing.value = false;
    keepTermsModalOpen.value = false;
    emit('error', {
        type: 'terms',
        error: error
    });
};

const closeCardModal = () => {
    showAddCardModal.value = false;
    isSubscribing.value = false;
    emit('cancel');
};

/**
 * RAC-10025: AddCardPopup's CTA now reads "Subscribe". When the card is
 * successfully attached we close the popup, reload the freshly-attached
 * payment method state, restore the pending plan selection, and then
 * subscribe immediately — no confirmation modal in between.
 */
const handleCardAdded = (eventData) => {
    const { success } = eventData || {};

    if (!success) {
        isSubscribing.value = false;
        emit('error', {
            type: 'card',
            error: eventData.error || 'Failed to add card'
        });
        return;
    }

    showAddCardModal.value = false;
    showTermsModal.value = false;
    keepTermsModalOpen.value = false;
    isSubscribing.value = true;

    const currentScrollY = window.scrollY;

    router.reload({
        only: getPaymentMethodProps(),
        preserveState: true,
        preserveScroll: true,
        onSuccess: async () => {
            await nextTick();
            requestAnimationFrame(() => {
                window.scrollTo(0, currentScrollY);
            });

            // Make sure the parent restores plan selection from sessionStorage
            // before we fire the subscribe request — otherwise the request
            // can land without plan_slug and silently downgrade to the
            // default plan (the RAC-10008 footgun).
            emit('restore-plan-selection');
            await nextTick();
            loadStoredPlanData();
            await nextTick();
            loadStoredPlanData();
            await new Promise(resolve => setTimeout(resolve, 200));
            loadStoredPlanData();

            subscribeToProtect();
        },
        onError: async () => {
            emit('restore-plan-selection');
            await nextTick();
            loadStoredPlanData();
            // Best-effort: still attempt to subscribe; the backend will
            // surface a card-required error if the attach actually failed.
            subscribeToProtect();
        }
    });
};

const subscribeToProtect = async () => {
    try {
        isSubscribing.value = true;
        
        // Get plan data from props or sessionStorage
        const planSlug = getPlanSlug()
        const billingCycle = getBillingCycle()

        // RAC-10335: never fire the subscribe request without a plan. Omitting plan_slug used
        // to silently subscribe the user to base RiskGuard. Surface an error and let them retry
        // rather than charging the wrong plan. (The backend now also rejects a missing plan.)
        if (!planSlug) {
            isSubscribing.value = false;
            emit('error', {
                type: 'plan',
                error: 'We could not start your subscription. Please try again.',
            });
            return;
        }

        // Determine redirect URL. RAC-10004: an explicit `successRedirectUrl`
        // prop wins (signup flow lands users on the new property's details
        // page). 86bc9e403: otherwise stay on the current page — the Pro checkout
        // is now in-place everywhere, including /pricing, so there is no longer a
        // redirect to payment-methods.index.
        const redirectUrl = props.successRedirectUrl || window.location.href;
        
        const expectedTierSlug = getExpectedTierSlug();
        const requestData = {
            redirect_url: redirectUrl,
            ...((props.alwaysSendTerms || !props.termsAccepted) && { terms_accepted: true }),
            ...(planSlug && { plan_slug: planSlug }),
            ...(billingCycle && { billing_cycle: billingCycle }),
            ...(expectedTierSlug && { expected_tier_slug: expectedTierSlug }),
            ...(getEnteredPropertyCount() != null && { property_count: getEnteredPropertyCount() }),
            ...(getStoredSuccessMessage() && { success_message: getStoredSuccessMessage() }),
        };
        
        const response = await axios.post(route('protect-subscription.subscribe'), requestData);
        
        if (keepTermsModalOpen.value) {
            keepTermsModalOpen.value = false;
            showTermsModal.value = false;
        }
        closeAllModals();
        
        // Clear sessionStorage
        sessionStorage.removeItem('pending_subscription_plan');
        sessionStorage.removeItem('subscription_success_message');
        pendingPlanFallback.value = null;
        
        // Get redirect URL and success message from response (use response URL if available, otherwise use the one we determined earlier)
        const finalRedirectUrl = response.data?.redirect_url || redirectUrl;
        const successMessage = response.data?.message || 'Subscription has been started successfully.';
        
        pendingApplication.value = null;
        isSubscribing.value = false;

        const deferReloadForPendingApiActivation = (() => {
            try {
                return sessionStorage.getItem('pending_api_paused_activation_property_id') !== null;
            } catch {
                return false;
            }
        })();

        if (deferReloadForPendingApiActivation) {
            emit('success', { successMessage: successMessage });
            return;
        }

        if (finalRedirectUrl !== window.location.href) {
            emit('success', {
                redirectUrl: finalRedirectUrl,
                successMessage: successMessage
            });
            router.visit(finalRedirectUrl);
        } else {
            // 86bcd9hg3: a full reload — Pro changes the top bar, every property's coverage
            // and the open tab, so a partial reload would leave them stale.
            router.reload({
                preserveScroll: true,
                onSuccess: () => {
                    emit('success', { successMessage: successMessage });
                }
            });
        }
    } catch (error) {
        isSubscribing.value = false;

        if (keepTermsModalOpen.value) {
            // Keep modal open on error
        }

        // 86bcd9hg3: the landlord's property count moved the price since the pop-up showed
        // it. Nothing was charged; the caller shows the new price and asks again.
        if (error.response?.status === 409 && error.response?.data?.tier_changed) {
            // The terms were already saved; close our pop-ups so only the caller's shows.
            closeAllModals();
            emit('tier-changed', {
                tierSlug: error.response.data.tier_slug,
                price: error.response.data.price,
                message: error.response.data.message,
            });
            return;
        }

        if (error.response?.status === 302 || error.response?.status === 301) {
            const redirectUrl = error.response?.headers?.location || '';
            if (redirectUrl.includes('payment-methods.create')) {
                emit('error', {
                    type: 'subscription',
                    error: 'Payment method is required. Please add a payment method first.'
                });
                return;
            }
        }
        
        const errorMessage = error.response?.data?.message 
            || error.response?.data?.error 
            || error.message 
            || 'An error occurred while subscribing. Please try again.';
        
        emit('error', {
            type: 'subscription',
            error: errorMessage
        });
    }
};

// Watch for props changes and reload stored data
watch(() => [props.planSlug, props.billingCycle, props.planPrice], () => {
    loadStoredPlanData();
}, { immediate: true })

// Load stored data on mount
onMounted(() => {
    loadStoredPlanData();
})

/**
 * 86bc9e403: the combined Pro checkout saves a new card inside its own pop-up (via
 * CardFields), which leaves props.hasPaymentMethod / props.userPaymentMethod STALE —
 * initiateSubscription would see "no card" and reopen the add-card flow. This mirrors
 * handleCardAdded's reload-then-subscribe: refresh the freshly-attached payment-method
 * props, then subscribe. Used only for the new-card path; an on-file card uses the normal
 * initiateSubscription (Case 1).
 */
const subscribeAfterCardSaved = async (planFallback = null) => {
    pendingPlanFallback.value = planFallback;
    isSubscribing.value = true;

    const currentScrollY = window.scrollY;

    router.reload({
        only: getPaymentMethodProps(),
        preserveState: true,
        preserveScroll: true,
        onSuccess: async () => {
            await nextTick();
            requestAnimationFrame(() => {
                window.scrollTo(0, currentScrollY);
            });

            loadStoredPlanData();
            await nextTick();

            subscribeToProtect();
        },
        onError: async () => {
            loadStoredPlanData();
            subscribeToProtect();
        }
    });
};

// Expose the subscription methods for parent components (and the combined Pro
// checkout pop-up) to call via ref.
defineExpose({
    initiateSubscription,
    subscribeAfterCardSaved
});
</script>

